import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// F70 · Fase 5 · slice para elementos decorativos del plano (paredes,
// columnas, barras, plantas, WC, entrada, cocina, escaleras). No son
// mesas: no tienen sesión ni cuenta. Solo geometría + estilo.
// La API viene de /api/plano-elements (GET) y /api/plano-elements/bulk.

const initialState = {
  isLoading: false,
  hasError: null,
  // Lista completa de elementos activos del restaurante (todas las zonas).
  // El componente filtra por zona antes de renderizar.
  elements: [],
  // Draft de cambios pendientes en el editor. Mismo patrón que
  // draftMesaEdits en ProductList — se acumula y se persiste con
  // `bulkSavePlanoElements` al pulsar "Guardar plano".
  draftEdits: {},        // { [elementId]: { pos?, span?, rotationDeg?, kind?, label?, color? } }
  // Elementos NUEVOS aún sin id de backend. Se envían con id=null en el bulk.
  draftNew: [],          // [{ tempId, kind, zonaId, posCol, posRow, ... }]
  // Elementos borrados en el draft (id → true). Se envían con deleted=true.
  draftDeleted: {},
};

const slice = createSlice({
  name: "planoElements",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.hasError = null; },
    hasError(state, action) { state.isLoading = false; state.hasError = action.payload; },
    getSuccess(state, action) {
      state.isLoading = false;
      state.elements = action.payload || [];
    },
    // Draft mutators — todos los cambios en el editor pasan por aquí.
    patchDraftEdit(state, action) {
      const { id, patch } = action.payload || {};
      if (!id || !patch) return;
      state.draftEdits[id] = { ...(state.draftEdits[id] || {}), ...patch };
    },
    addDraftNew(state, action) {
      const item = action.payload;
      if (!item) return;
      state.draftNew.push(item);
    },
    updateDraftNew(state, action) {
      const { tempId, patch } = action.payload || {};
      const idx = state.draftNew.findIndex((x) => x.tempId === tempId);
      if (idx < 0) return;
      state.draftNew[idx] = { ...state.draftNew[idx], ...patch };
    },
    removeDraftNew(state, action) {
      const tempId = action.payload;
      state.draftNew = state.draftNew.filter((x) => x.tempId !== tempId);
    },
    markDraftDeleted(state, action) {
      const id = action.payload;
      if (id) state.draftDeleted[id] = true;
    },
    unmarkDraftDeleted(state, action) {
      const id = action.payload;
      if (id && state.draftDeleted[id]) delete state.draftDeleted[id];
    },
    clearDraft(state) {
      state.draftEdits = {};
      state.draftNew = [];
      state.draftDeleted = {};
    },
  },
});

export default slice.reducer;
export const {
  patchDraftEdit,
  addDraftNew,
  updateDraftNew,
  removeDraftNew,
  markDraftDeleted,
  unmarkDraftDeleted,
  clearDraft,
} = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getPlanoElements(zonaId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const q = zonaId != null ? `?zonaId=${zonaId}` : "";
      const res = await axios.get(`plano-elements${q}`);
      dispatch(slice.actions.getSuccess(res?.data?.elements || []));
      return res?.data?.elements || [];
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      dispatch(slice.actions.hasError(err?.message || "Error cargando elementos"));
      throw err;
    }
  };
}

export function bulkSavePlanoElements(items, zonaId) {
  return async (dispatch) => {
    try {
      const res = await axios.post("plano-elements/bulk", {
        zonaId,
        elements: items || [],
      });
      const elements = res?.data?.elements || [];
      dispatch(slice.actions.getSuccess(elements));
      dispatch(slice.actions.clearDraft());
      return elements;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      dispatch(slice.actions.hasError(err?.message || "Error guardando"));
      throw err;
    }
  };
}
